import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useMessage } from '../contexts/MessageContext';
import { useAuth  } from '../contexts/AuthContext';
import { Loading } from './common/Loading';
import { TextField, Button, Grid } from "@mui/material";

const apiUrl = window._env_?.REACT_APP_API_URL || "https://app.mylocaleventrentals.com/api";

const CustomerNew = () => {
  const { showSuccess, showError, showWarning, showInfo } = useMessage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "ON",
    zipCode: "",
    company: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingCustomer, setExistingCustomer] = useState(null);

  const navigate = useNavigate();
  const logout = useAuth();
  const token = localStorage.getItem("token");

  // Check for existing customer when email changes
  useEffect(() => {
    const token = localStorage.getItem('token');
    if(!token){
      logout();
      navigate('/login');
    }
    const checkExistingCustomer = async () => {
      if (formData.email && formData.email.trim() !== '') {
        try {
          const response = await axios.get(`${apiUrl}/customers/search`, {
            params: { email: formData.email.toLowerCase().trim() },
            headers: { "x-auth-token": token }
          });
          setExistingCustomer(response.data.exists ? response.data.customer : null);
          if (response.data.exists) {
            showInfo(`Existing customer with same email: ${response.data.customer.email}`);
   
          }
        } catch (error) {
          console.error('Error checking existing customer:', error);
          showError('Error checking existing customer');
          setExistingCustomer(null);
        }
      } else {
        setExistingCustomer(null);
      }
    };

    const debounceTimer = setTimeout(checkExistingCustomer, 500);
    return () => clearTimeout(debounceTimer);
  }, [formData.email, token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstname.trim()) {
      newErrors.firstname = 'Name is required';
    }
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    if (existingCustomer) {
      showWarning('A customer with this email already exists. Please use a different email.');
      return;
    }

    setIsSubmitting(true);
    setLoading(true);
    try {
      const response = await axios.post(`${apiUrl}/customers/new`, {
        ...formData,
        email: formData.email.trim() || null
      }, {
        headers: { "x-auth-token": token }
      });
      
      const addressData = await axios.post(`${apiUrl}/address`, {
        line1: formData.address,
        city: formData.city,
        state: formData.state,
        postal_code: formData.zipCode
      }, {
        headers: { "x-auth-token": token }
      });

      const customerAddress = await axios.post(`${apiUrl}/customer-address`, {
        customer_id: response.data.data.uuid,
        address_id: addressData.data.data.uuid,
        is_primary: true,
      }, {
        headers: { "x-auth-token": token }
      });

      showSuccess('Customer created successfully!');
      setFormData({
        firstname: "",
        lastname: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        zipCode: "",
        company: ""
      });
      navigate(`/customer/${response.data.data.uuid}/view`);

    } catch (error) {
     
      console.error('Error creating customer:', error);
      showError(error.response?.data?.message || error.message);
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All changes will be lost.')) {
      navigate('/CustomerList');
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="grid p-6 my-6 grid-cols-1 shadow-lg rounded md:rounded-lg bg-white">
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-800">Add Customer</h2>

      
      {/* Existing Customer Warning */}
      {existingCustomer && (
        <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Customer Already Exists
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  A customer with email <strong>{existingCustomer.email}</strong> already exists.
                  {existingCustomer.name && ` Name: ${existingCustomer.name}`}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {/* Name Field */}
          <div className="sm:col-span-2">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              First Name *
            </label>
            <input
              type="text"
              id="name"
              name="firstname"
              value={formData.firstname}
              onChange={handleInputChange}
              className={`mt-1 block w-full border rounded-md shadow-sm p-2 ${
                errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
              }`}
              placeholder="Enter customer's first name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Last Name *
            </label>
            <input
              type="text"
              id="name"
              name="lastname"
              value={formData.lastname}
              onChange={handleInputChange}
              className={`mt-1 block w-full border rounded-md shadow-sm p-2 ${
                errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
              }`}
              placeholder="Enter customer's last name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Email Field */}
          <div className="sm:col-span-2">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`mt-1 block w-full border rounded-md shadow-sm p-2 ${
                errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
              }`}
              placeholder="customer@example.com"
            />
            {errors.email ? (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            ) : (
              <p className="mt-1 text-sm text-gray-500">Optional - leave blank if not available</p>
            )}
          </div>

          {/* Phone Field */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:border-blue-500 focus:ring-blue-500"
              placeholder="(555) 123-4567"
            />
          </div>

          {/* Company Field */}
          <div>
            <label htmlFor="company" className="block text-sm font-medium text-gray-700">
              Company
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:border-blue-500 focus:ring-blue-500"
              placeholder="Company name"
            />
          </div>

          {/* Address Field */}
          <div className="sm:col-span-2">
            <label htmlFor="address" className="block text-sm font-medium text-gray-700">
              Address
            </label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:border-blue-500 focus:ring-blue-500"
              placeholder="Street address"
            />
          </div>

          {/* City, State, Zip */}
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700">
              City
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:border-blue-500 focus:ring-blue-500"
              placeholder="City"
            />
          </div>

          <div>
            <label htmlFor="state" className="block text-sm font-medium text-gray-700">
              State
            </label>
            <input
              type="text"
              id="state"
              name="state"
              value={formData.state}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:border-blue-500 focus:ring-blue-500"
              placeholder="State"
            />
          </div>

          <div>
            <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700">
              ZIP Code
            </label>
            <input
              type="text"
              id="zipCode"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:border-blue-500 focus:ring-blue-500"
              placeholder="ZIP code"
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || existingCustomer}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Creating...' : 'Create Customer'}
          </button>
        </div>
      </form>

      {/* Help Text */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          Fields marked with * are required. Email is optional but recommended for better customer management.
        </p>
      </div>
    </div>
  );
};

export default CustomerNew;